import {
  Component,
  OnDestroy,
  Input,
  SimpleChanges,
  OnChanges,
  Inject,
  OnInit,
  NgZone,
  PLATFORM_ID,
} from "@angular/core";
import { colorMapping, colorMappingStack } from "./stacked-bar-chart.model";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { DashboardService } from "../dashboard.service";
import { ChartDataService } from "../../../helpers/chart-data.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { am5ChartLicense } from "../../../app.constants";

@Component({
  selector: "app-stacked-bar-chart",
  templateUrl: "./stacked-bar-chart.component.html",
  styleUrl: "./stacked-bar-chart.component.scss",
})
export class StackedBarChartComponent implements OnDestroy, OnChanges, OnInit {
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() categoryIds: number[];
  @Input() companyId!: number;
  @Input() chartContainerId: string =
    "barChartDiv" + Math.random().toString(36).substring(2, 9); // Unique ID for chart container
  exitingCategories: number[];
  @Input() apiType: string = "stack"; // 'count' for /dashboardBookingsperStore, 'percentage' for /dashboardBookingsperStorePercentage

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["categoryIds"] ||
      changes["companyId"] ||
      changes["apiType"] ||
      changes["startDate"]?.currentValue ||
      changes["endDate"]?.currentValue
    ) {
      this.loadChartData();
    }
  }

  private root: am5.Root | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
    private dashboardService: DashboardService,
    private chartDataService: ChartDataService,
    private _snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const companyIdval = localStorage.getItem("companyId");
    this.companyId = companyIdval ? Number.parseInt(companyIdval, 10) : 0;
    this.exitingCategories = this.categoryIds;
  }

  loadChartData() {
    const params = {
      categoryIds: this.categoryIds,
      startDate: this.startDate,
      endDate: this.endDate,
      companyId: this.companyId,
    };
    if (
      !params.startDate ||
      !params.endDate ||
      !params.categoryIds ||
      !params.companyId
    ) {
      return;
    }

    // Call appropriate API based on apiType
    const apiCall =
      this.apiType === "stack"
        ? this.dashboardService.getDashboardBookings(params)
        : this.dashboardService.getDashboardBookingsStore(params);

    apiCall.subscribe(
      (data: any[]) => {
        if (this.root) {
          this.root.dispose();
        }
        if (data?.length === 0 || !data) {
          return;
        }

        const transformedData: Record<string, any>[] = [];
        let processedData = [];
        data.forEach((item) => {
          let existingXValue = transformedData.find(
            (d) => d["xValue"] === item.xValue,
          );

          if (!existingXValue) {
            existingXValue = { xValue: item.xValue }; // Create a new object for this xValue
            transformedData.push(existingXValue);
          }

          // Assign the yValue to the corresponding yLabel (series) in the object
          existingXValue[item.yLabel] = item.yValue;
        });
        // Group by xValue and accumulate yValues for each yLabel
        if (this.apiType === "stack") {
          processedData =
            this.chartDataService.convertXValueFromYearMonthToMonthName(
              transformedData,
            );
        } else processedData = transformedData;

        this.createChart(processedData); // Pass transformed data to createChart
      },
      (error) => {
        this._snackBar.open(
          "Failed to load chart data. Please try again later.",
          "Close",
          {
            duration: 3000,
            horizontalPosition: "center",
            verticalPosition: "top",
          },
        );
        return;
      },
    );
  }

  createChart(data: any[]): void {
    this.browserOnly(() => {
      const element = document.getElementById(this.chartContainerId);
      if (!element) return;

      if (this.root) this.root.dispose();

      am5.addLicense(am5ChartLicense);
      this.root = am5.Root.new(this.chartContainerId);
      if (!this.root) return;

      this.root.setThemes([]);
      this.root._logo?.dispose();

      // Root vertical container = chart at top, legend at bottom
      const mainContainer = this.root.container.children.push(
        am5.Container.new(this.root, {
          layout: this.root.verticalLayout, // <<< vertical layout
          width: am5.percent(100),
          height: am5.percent(100),
        }),
      );

      // Chart container
      const chart = mainContainer.children.push(
        am5xy.XYChart.new(this.root, {
          width: am5.percent(100),
          height: am5.percent(100),
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          layout: this.root.horizontalLayout,
        }),
      );

      // Optional horizontal scrollbar (enable if many categories)
      chart.set(
        "scrollbarX",
        am5.Scrollbar.new(this.root, { orientation: "horizontal" }),
      );

      const xAxisRenderer = am5xy.AxisRendererX.new(this.root, {
        minorGridEnabled: false,
        minGridDistance: 10,
      });

      // 👇 Add gaps between categories (0 = no gap, 0.5 = huge gap)
      xAxisRenderer.setAll({
        cellStartLocation: 0.25, // start a bit to the right
        cellEndLocation: 0.75, // end a bit to the left
      });

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          categoryField: "xValue",
          renderer: xAxisRenderer,
        }),
      );

      xAxis.get("renderer").grid.template.setAll({ visible: false });

      // Put categories on axis
      xAxis.data.setAll(data);

      // Calculate max Y from transformed data
      let maxY = 0;

      data.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (key !== "xValue") {
            const val = Number(row[key]) || 0;
            if (val > maxY) maxY = val;
          }
        });
      });

      const finalMaxY = maxY + 50; // Calculate max Y value for scaling

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          min: 0,
          max: finalMaxY,
          strictMinMax: false,
          renderer: am5xy.AxisRendererY.new(this.root, { strokeOpacity: 0.1 }),
        }),
      );

      // Unique series names from keys (exclude xValue)
      const uniqueYLabels = [
        ...new Set(
          data.flatMap((item) =>
            Object.keys(item).filter((k) => k !== "xValue"),
          ),
        ),
      ];

      // Define the desired stack order: API (bottom) then IFRAME (top)
      const desiredOrder = [
        "Leenbakker meubelmontage Api",
        "Leenbakker meubelmontage Module",
      ];

      const orderedLabels = [
        ...desiredOrder.filter((n) => uniqueYLabels.includes(n)),
        ...uniqueYLabels.filter((n) => !desiredOrder.includes(n)), // any extra series at the end
      ];

      // Create either stacked columns or lines
      orderedLabels.forEach((yLabel) => {
        if (this.apiType === "stack") {
          this.makeStackedColumnSeries(
            yLabel,
            yLabel,
            chart,
            xAxis,
            yAxis,
            data,
          );
        } else {
          this.makeLineSeries(yLabel, yLabel, chart, xAxis, yAxis, data);
        }
      });

      // Optional legend
      const legend = mainContainer.children.push(
        am5.Legend.new(this.root, {
          nameField: "name",
          fillField: "fill",
          strokeField: "stroke",
          centerX: am5.percent(50),
          x: am5.percent(50),
          paddingTop: 20,
        }),
      );

      legend.data.setAll(chart.series.values);

      // Cursor (handy for tooltips)
      chart.set("cursor", am5xy.XYCursor.new(this.root, { behavior: "none" }));

      chart.appear(1000, 100);
    });
  }

  makeLineSeries(
    name: string,
    fieldName: string,
    chart: any,
    xAxis: any,
    yAxis: any,
    data: any[],
  ): void {
    if (!this.root) {
      console.error("Root element is not defined");
      return;
    }

    // Create the LineSeries
    const series = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: fieldName,
        categoryXField: "xValue",
      }),
    );

    // Set the color for the line, bullet, and label from color mapping
    const seriesColor = am5.color(colorMapping[name] || "#000000"); // Default to black if color is not found

    // Ensure the line stroke color is explicitly set, reapplying if necessary
    series.strokes.template.setAll({
      strokeWidth: 2,
      stroke: seriesColor, // Explicitly apply seriesColor to the line stroke
    });

    // Another direct way to set the line color to ensure it's applied - ADDED
    series.set("stroke", seriesColor);

    // Prepare data for the specific yLabel
    const seriesData = data.map((item) => ({
      xValue: item.xValue,
      [fieldName]: item[fieldName] || 0,
    }));

    series.data.setAll(seriesData);

    // Set up the tooltip with explicit color properties for background and text
    const tooltip = am5.Tooltip.new(this.root!, {
      labelText: `{valueYField}: {categoryX}: {${fieldName}}`,
    });

    // Apply color settings to tooltip background and label directly
    tooltip.get("background")!.adapters.add("fill", () => seriesColor); // Set the background color
    tooltip.get("background")!.adapters.add("stroke", () => seriesColor); // Set the border color
    tooltip.label.adapters.add("fill", () => am5.color(0xffffff)); // Set the text color to white

    // Apply the tooltip to the series
    series.set("tooltip", tooltip);

    // Add a cursor to improve interaction
    chart.set("cursor", am5xy.XYCursor.new(this.root!, {}));

    // Disables horizontal and vertical scrollbar
    chart.set("scrollbarX", null);
    chart.set("scrollbarY", null);

    // Add circular markers to each data point, matching the line color
    series.bullets.push(() =>
      am5.Bullet.new(this.root!, {
        sprite: am5.Circle.new(this.root!, {
          radius: 4,
          fill: seriesColor,
          stroke: seriesColor,
        }),
      }),
    );

    series.appear();
  }

  makeStackedColumnSeries(
    name: string,
    fieldName: string,
    chart: any,
    xAxis: any,
    yAxis: any,
    data: any[],
  ): void {
    if (!this.root) return;

    const color = am5.color(colorMappingStack[name] || "#777");

    // ✅ Correct object to create: ColumnSeries (not XYChart!)
    const series = chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name,
        xAxis,
        yAxis,
        valueYField: fieldName,
        categoryXField: "xValue",
        stacked: true, // important
      }),
    );

    // Column style
    series.columns.template.setAll({
      fill: color,
      stroke: color,
      width: am5.percent(100), // full width
      tooltipText: "{name}\n{categoryX}: {valueY}",
      cornerRadiusTL: 0,
      cornerRadiusTR: 0,
    });

    // Tooltip
    const tooltip = am5.Tooltip.new(this.root, {
      labelText: "{name}\n{categoryX}: {valueY}",
    });

    tooltip.get("background")!.setAll({
      fill: color,
      stroke: color,
    });

    tooltip.label.setAll({
      fill: am5.color(0xffffff),
    });

    series.set("tooltip", tooltip);

    // Add a cursor to improve interaction
    chart.set("cursor", am5xy.XYCursor.new(this.root!, {}));

    // Disables horizontal and vertical scrollbar
    chart.set("scrollbarX", null);
    chart.set("scrollbarY", null);
    // Data
    const seriesData = data.map((item) => ({
      xValue: item.xValue,
      [fieldName]: item[fieldName] || 0,
    }));

    series.data.setAll(seriesData);

    series.appear();
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose(); // Properly dispose of the chart root to clean up resources
    }
  }

  browserOnly(f: () => void) {
    if (typeof window !== "undefined") {
      f();
    }
  }
}
